import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import styled,{keyframes} from "styled-components";

import ComponentElement from "./ComponentElement";
import { componentTypes } from "./componentTypes";
import isFirstDayOfMonth from "date-fns/isFirstDayOfMonth/index.js";
import QuestionLibraryModal from "./QuestionLibraryModal";
import AISurveyGuideModal from "./AISurveyGuideModal";

// Example reorder logic
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

// For demonstration, reusing your group function
function groupComponentTypesByCategory(typesObj) {
  const grouped = {};
  Object.entries(typesObj).forEach(([key, def]) => {
    const cat = def.category || "core";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(key);
  });
  return grouped;
}

// Helper function to determine if an item is an end_screen
const isEndScreen = (item) => item.type === 'end_screen';

// Helper function to get the index of the first end_screen in the list
const getFirstEndScreenIndex = (items) => {
  return items.findIndex(item => isEndScreen(item));
};

// Function to create a deep copy of an object
const deepCopy = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};

const groupedTypes = groupComponentTypesByCategory(componentTypes);
const allToolKeys = Object.values(groupedTypes).flat();

const SurveyQuestionsTab = forwardRef(({
  title,
  setTitle,
  description,
  setDescription,
  droppedItems,
  setDroppedItems,
  selectedItem,
  setSelectedItem,
  factors,
  categories,
  setCategories,
  uniqueIdCounterRef,
  endScreenItems,
  setEndScreenItems
}, ref) => {
  const [isLibraryModalOpen, setIsLibraryModalOpen] = useState(false);
  const [isAIGuideModalOpen, setIsAIGuideModalOpen] = useState(false);
  
  // Add a useEffect to monitor changes to endScreenItems
  useEffect(() => {
    console.log("endScreenItems changed:", endScreenItems.map(item => `${item.uniqueId} (${item.type})`));
  }, [endScreenItems]);
  
  // Wrap setEndScreenItems to add debugging
  const updateEndScreenItems = (newItems) => {
    console.log("Setting endScreenItems to:", Array.isArray(newItems) ? 
      newItems.map(item => `${item.uniqueId} (${item.type})`) : 
      'not an array');
    setEndScreenItems(newItems);
  };
  
  // Function to check if an ID already exists to prevent duplicates
  const isIdUnique = useCallback((id) => {
    const existsInDroppedItems = droppedItems.some(item => item.uniqueId === id);
    const existsInEndScreenItems = endScreenItems.some(item => item.uniqueId === id);
    return !existsInDroppedItems && !existsInEndScreenItems;
  }, [droppedItems, endScreenItems]);
  
  // Function to get a new unique ID, wrapped in useCallback for stability
  const getNewUniqueId = useCallback(() => {
    // Ensure we have a valid counter reference
    if (!uniqueIdCounterRef || typeof uniqueIdCounterRef.current !== 'number') {
      console.error("Invalid uniqueIdCounterRef provided");
      throw new Error("Invalid uniqueIdCounterRef");
    }
    
    // Get all existing IDs for logging
    const existingIds = [
      ...droppedItems.map(item => item.uniqueId),
      ...endScreenItems.map(item => item.uniqueId)
    ];
    console.log("Current existing IDs:", existingIds);
    
    // Generate IDs until we find a unique one
    let id;
    let attempts = 0;
    do {
      id = `item-${uniqueIdCounterRef.current}`;
      uniqueIdCounterRef.current += 1;
      attempts++;
      
      // Safety check to prevent infinite loops
      if (attempts > 1000) {
        console.error("Failed to generate unique ID after 1000 attempts");
        id = `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        break;
      }
    } while (!isIdUnique(id));
    
    console.log("Generated new unique ID:", id, "after", attempts, "attempts");
    return id;
  }, [uniqueIdCounterRef, isIdUnique, droppedItems, endScreenItems]);
  
  // Expose methods to parent component using useImperativeHandle
  useImperativeHandle(ref, () => ({
    // Method to check if the selected item is in the endScreenItems array
    isSelectedItemEndScreen: () => {
      if (!selectedItem) return false;
      
      // Simple check: if the type is 'end_screen', it's an end screen
      if (selectedItem.type === 'end_screen') {
        return true;
      }
      
      // Additional check: is it in the endScreenItems array
      const isInEndScreenItems = endScreenItems.some(item => item.uniqueId === selectedItem.uniqueId);
      console.log("Checking if item is end screen:", selectedItem.uniqueId, isInEndScreenItems);
      
      return isInEndScreenItems;
    },
    
    // Method to get all end screen uniqueIds for debugging
    getEndScreenIds: () => {
      return endScreenItems.map(item => item.uniqueId);
    },
    
    // Method to remove the selected item from endScreenItems
    removeSelectedEndScreen: () => {
      if (!selectedItem) {
        console.log("No selected item to remove");
        return;
      }
      
      const selectedItemId = selectedItem.uniqueId;
      console.log("Removing end screen with ID:", selectedItemId);
      
      // Check if this ID exists in the endScreenItems array
      const exists = endScreenItems.some(item => item.uniqueId === selectedItemId);
      if (!exists) {
        console.log("End screen not found in endScreenItems:", selectedItemId);
        console.log("Current end screen IDs:", endScreenItems.map(item => item.uniqueId));
        return;
      }
      
      // Create a new array WITHOUT the item to be removed
      const updatedEndScreens = endScreenItems.filter(item => item.uniqueId !== selectedItemId);
      console.log("Filtered end screens:", updatedEndScreens.map(item => item.uniqueId));
      
      // If no end screens will remain, create a default one
      if (updatedEndScreens.length === 0) {
        console.log("No end screens remain, creating default");
        
        const defaultEndScreen = {
          uniqueId: getNewUniqueId(),
          type: 'end_screen',
          properties: {
            blocks: [
              { type: "heading", level: "h2", content: "Thank You For Completing The Survey" },
              { type: "paragraph", content: "Your feedback is valuable to us and will help us improve our services." }
            ]
          },
          conditionalConfig: componentTypes['end_screen'].conditionalConfig
        };
        
        console.log("Created default end screen with ID:", defaultEndScreen.uniqueId);
        
        // Update with the new default screen
        updateEndScreenItems([defaultEndScreen]);
      } else {
        // Update with the filtered array
        updateEndScreenItems(updatedEndScreens);
      }
    }
  }), [selectedItem, endScreenItems, getNewUniqueId]);
  
  // Synchronize counter with existing items on mount
  useEffect(() => {
    // Extract numeric parts from existing IDs to find the max value
    const getAllIds = [...droppedItems, ...endScreenItems].map(item => item.uniqueId);
    
    // If there are existing items, ensure counter is greater than the highest ID
    if (getAllIds.length > 0) {
      const numericIds = getAllIds
        .filter(id => id && id.startsWith('item-'))
        .map(id => parseInt(id.replace('item-', ''), 10))
        .filter(num => !isNaN(num));
      
      if (numericIds.length > 0) {
        const maxId = Math.max(...numericIds);
        // Set counter to max ID + 1 if it's currently less than or equal to maxId
        if (uniqueIdCounterRef.current <= maxId) {
          console.log(`Adjusting uniqueIdCounter from ${uniqueIdCounterRef.current} to ${maxId + 1} based on existing IDs`);
          uniqueIdCounterRef.current = maxId + 1;
        }
      }
    }
  }, [droppedItems, endScreenItems, uniqueIdCounterRef]); // Update counter when items change
  
  // Create a ref to track whether initialization has run
  const initRef = useRef(false);
  
  // On component mount, initialize with a default end screen if none exists
  useEffect(() => {
    // Only run once and only if endScreenItems is empty
    if (!initRef.current && endScreenItems.length === 0) {
      initRef.current = true;
      
      // Create a default InnerLogic end screen
      const defaultEndScreen = {
        uniqueId: getNewUniqueId(),
        type: 'end_screen',
        properties: {
          blocks: [
            { type: "heading", level: "h2", content: "Thank You For Completing The Survey" },
            { type: "paragraph", content: "Your feedback is valuable to us and will help us improve our services." }
          ]
        },
        conditionalConfig: componentTypes['end_screen'].conditionalConfig
      };
      
      updateEndScreenItems([defaultEndScreen]);
    }
  }, [endScreenItems, getNewUniqueId, updateEndScreenItems]); // Dependencies needed for initialization

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    console.log("Drag end:", result);

    // Handle drag between different droppable areas
    if (source.droppableId !== destination.droppableId) {
      // Dragging from toolMenu to componentArea
      if (source.droppableId === "toolMenu" && destination.droppableId === "componentArea") {
        // Get the type key from the index in allToolKeys
        const draggedTypeKey = allToolKeys[source.index];
        console.log("Dragging from toolMenu to componentArea:", draggedTypeKey);
        
        const typeDef = componentTypes[draggedTypeKey];
        if (!typeDef) {
          console.error("Type definition not found for:", draggedTypeKey);
          return;
        }

        // Build default properties
        const defaultProperties = {};
        Object.entries(typeDef.properties).forEach(([propKey, propDef]) => {
          defaultProperties[propKey] = propDef.default;
        });

        const newItem = {
          uniqueId: getNewUniqueId(),
          type: draggedTypeKey,
          properties: defaultProperties,
          conditionalConfig: typeDef.conditionalConfig
        };
        
        console.log("Adding new item to componentArea:", newItem.uniqueId, newItem.type);

        // Handle special dropdown types
        if (draggedTypeKey === 'dropdown') {
          // For default category, check if it already has a value
          if (defaultProperties.category) {
            // Find the category and get its type
            const categoryObj = categories.find(cat => cat.name === defaultProperties.category);
            if (categoryObj) {
              // Store the category type in properties
              defaultProperties.categoryType = categoryObj.type;
              
              // Check if it's a date category
              if (categoryObj.type === 'date') {
                newItem.type = 'date';
              }
              // Otherwise, check if it's a multiSelect based on Allow multiple selections
              else if (defaultProperties['Allow multiple selections'] === true) {
                newItem.type = 'multiSelect';
              }
            }
          } 
          // If no category but Allow multiple selections is true
          else if (defaultProperties['Allow multiple selections'] === true) {
            newItem.type = 'multiSelect';
          }
        }
        
        // Add the new item to the items array at the destination index
        const newDroppedItems = Array.from(droppedItems);
        newDroppedItems.splice(destination.index, 0, newItem);
        setDroppedItems(newDroppedItems);
        return;
      }
      
      // Dragging from toolMenu to endScreenArea
      if (source.droppableId === "toolMenu" && destination.droppableId === "endScreenArea") {
        // Get the type key from the index in allToolKeys
        const draggedTypeKey = allToolKeys[source.index];
        console.log("Dragging from toolMenu to endScreenArea:", draggedTypeKey);
        
        // Only allow end_screen in the endScreenArea
        if (draggedTypeKey !== 'end_screen') {
          console.log("Attempted to drag non-end-screen to endScreenArea");
          return;
        }
        
        const typeDef = componentTypes[draggedTypeKey];
        
        // Build default properties
        const defaultProperties = {};
        Object.entries(typeDef.properties).forEach(([propKey, propDef]) => {
          defaultProperties[propKey] = propDef.default;
        });
        
        const newItem = {
          uniqueId: getNewUniqueId(),
          type: draggedTypeKey,
          properties: defaultProperties,
          conditionalConfig: typeDef.conditionalConfig
        };
        
        console.log("Adding new end screen to endScreenArea:", newItem.uniqueId);
        
        // Create a new array with the new end screen at the specified index
        const newEndScreenItems = Array.from(endScreenItems);
        newEndScreenItems.splice(destination.index, 0, newItem);
        updateEndScreenItems(newEndScreenItems);
        return;
      }
      
      // Add handling for dragging from endScreenArea to componentArea (not allowed)
      if (source.droppableId === "endScreenArea" && destination.droppableId === "componentArea") {
        // End screens can only be in the endScreenArea, so cancel this operation
        console.log("Attempted to drag end screen to componentArea - not allowed");
        return;
      }
      
      // Add handling for dragging from componentArea to endScreenArea (not allowed)
      if (source.droppableId === "componentArea" && destination.droppableId === "endScreenArea") {
        // Only end screens can be in the endScreenArea, so cancel this operation
        console.log("Attempted to drag component to endScreenArea - not allowed");
        return;
      }
    }
    
    // Reordering within componentArea
    if (source.droppableId === "componentArea" && destination.droppableId === "componentArea") {
      console.log("Reordering within componentArea");
      
      // Use the standard reorder function
      const reorderedItems = reorder(
        droppedItems,
        source.index,
        destination.index
      );
      
      setDroppedItems(reorderedItems);
      return;
    }
    
    // Reordering within endScreenArea
    if (source.droppableId === "endScreenArea" && destination.droppableId === "endScreenArea") {
      console.log("Reordering within endScreenArea");
      
      // Use the standard reorder function
      const reorderedEndScreens = reorder(
        endScreenItems,
        source.index,
        destination.index
      );
      
      updateEndScreenItems(reorderedEndScreens);
      return;
    }
  };

  const handleSelectItem = (item) => {
    if (!item || !item.uniqueId) {
      console.error("Invalid item selected:", item);
      return;
    }
    
    console.log("Selected item:", item.uniqueId, "type:", item.type);
    
    // Check if this item is in regular components
    const isInDroppedItems = droppedItems.some(comp => comp.uniqueId === item.uniqueId);
    
    // Check if this item is in end screen components
    const isInEndScreenItems = endScreenItems.some(endItem => endItem.uniqueId === item.uniqueId);
    
    console.log(`Item location - In regular components: ${isInDroppedItems}, In end screens: ${isInEndScreenItems}`);
    
    // Update the selected item
    setSelectedItem(item);
  };

  const findFactor = (uniqueId,factors) => {
   for(let i=0;i<factors.length;i++){
     if(factors[i].questionIds.includes(uniqueId)){
       return factors[i].title
     }
   }

   return null
  }

  // Function to handle duplicating a question component
  const handleDuplicateItem = (item, isEndScreen = false) => {
    if (!item || !item.uniqueId) {
      console.error("Invalid item for duplication:", item);
      return;
    }
    
    console.log("Duplicating item:", item.uniqueId, "type:", item.type);
    
    // Create a deep copy of the item to duplicate
    const duplicatedItem = deepCopy(item);
    
    // IMPORTANT: Assign a new uniqueId to the duplicated item
    duplicatedItem.uniqueId = getNewUniqueId();
    console.log("New duplicated item ID:", duplicatedItem.uniqueId);
    
    // Determine if this is an end screen component
    const isEndScreenComponent = item.type === 'end_screen' || isEndScreen;
    
    if (isEndScreenComponent) {
      console.log("Duplicating as end screen");
      
      // Find the item in the end screen items
      const indexInEndScreens = endScreenItems.findIndex(i => i.uniqueId === item.uniqueId);
      
      if (indexInEndScreens === -1) {
        console.log("Original end screen not found for duplication");
        return;
      }
      
      // Create a new array of end screen items with the duplicated item
      const newEndScreenItems = [...endScreenItems];
      newEndScreenItems.splice(indexInEndScreens + 1, 0, duplicatedItem);
      
      // Update the end screen items
      updateEndScreenItems(newEndScreenItems);
    } else {
      console.log("Duplicating as regular component");
      
      // Find the item in the dropped items
      const indexInDroppedItems = droppedItems.findIndex(i => i.uniqueId === item.uniqueId);
      
      if (indexInDroppedItems === -1) {
        console.log("Original component not found for duplication");
        return;
      }
      
      // Create a new array of dropped items with the duplicated item
      const newDroppedItems = [...droppedItems];
      newDroppedItems.splice(indexInDroppedItems + 1, 0, duplicatedItem);
      
      // Update the dropped items
      setDroppedItems(newDroppedItems);
    }
    
    // Select the newly duplicated item
    setSelectedItem(duplicatedItem);
  };

  // Function to handle adding a question from the library
  const handleAddQuestionFromLibrary = (question) => {
    const typeDef = componentTypes[question.type];
    
    if (!typeDef) {
      console.error(`Question type ${question.type} not found in component types`);
      return;
    }

    // Build default properties
    const defaultProperties = {};
    Object.entries(typeDef.properties).forEach(([propKey, propDef]) => {
      defaultProperties[propKey] = propDef.default;
    });

    // Override the question text with the library question
    defaultProperties.question = question.question;

    const newItem = {
      uniqueId: getNewUniqueId(),
      type: question.type,
      properties: defaultProperties,
      conditionalConfig: typeDef.conditionalConfig
    };

    // Handle special dropdown types:
    // If it's a dropdown, check if we need to change the type
    if (question.type === 'dropdown') {
      // For default category, check if it already has a value
      if (defaultProperties.category) {
        // Find the category and get its type
        const categoryObj = categories.find(cat => cat.name === defaultProperties.category);
        if (categoryObj) {
          // Store the category type in properties
          defaultProperties.categoryType = categoryObj.type;
          
          // Check if it's a date category
          if (categoryObj.type === 'date') {
            newItem.type = 'date';
          }
          // Otherwise, check if it's a multiSelect based on Allow multiple selections
          else if (defaultProperties['Allow multiple selections'] === true) {
            newItem.type = 'multiSelect';
          }
        }
      } 
      // If no category but Allow multiple selections is true
      else if (defaultProperties['Allow multiple selections'] === true) {
        newItem.type = 'multiSelect';
      }
    }

    // If this is an end_screen, add it to endScreenItems
    if (question.type === 'end_screen') {
      updateEndScreenItems([...endScreenItems, newItem]);
    } else {
      // Otherwise add it to the regular items
      setDroppedItems([...droppedItems, newItem]);
    }
  };

  // Function to handle updating a question's properties
  const handleUpdateQuestion = (uniqueId, newProperties) => {
    // Update a question in the droppedItems array
    const updatedItems = droppedItems.map(item => {
      if (item.uniqueId === uniqueId) {
        return {
          ...item,
          properties: {
            ...item.properties,
            ...newProperties
          }
        };
      }
      return item;
    });
    
    setDroppedItems(updatedItems);
  };
  
  // Function to handle adding a new question from AI suggestions
  const handleAddAISuggestedQuestion = (questionData) => {
    const typeDef = componentTypes[questionData.type];
    
    if (!typeDef) {
      console.error(`Question type ${questionData.type} not found in component types`);
      return;
    }

    // Build default properties
    const defaultProperties = {};
    Object.entries(typeDef.properties).forEach(([propKey, propDef]) => {
      defaultProperties[propKey] = propDef.default;
    });

    // Override with the question text
    const properties = {
      ...defaultProperties,
      question: questionData.properties.question
    };

    const newItem = {
      uniqueId: getNewUniqueId(),
      type: questionData.type,
      properties: properties,
      conditionalConfig: typeDef.conditionalConfig
    };

    // Add to the survey
    setDroppedItems([...droppedItems, newItem]);
  };
  
  // Function to handle updating factor assignments
  const handleMoveQuestionToFactor = (questionId, factorName) => {
    // Find the factor object
    const targetFactor = factors.find(f => f.title === factorName);
    
    if (!targetFactor) {
      console.error(`Factor "${factorName}" not found`);
      return;
    }
    
    // This would need to call a parent function to update the factor
    // For now, we'll just log what would happen
    console.log(`Would move question ${questionId} to factor ${factorName}`);
  };
  
  // Function to create a new factor
  const handleAddFactor = (factorDetails) => {
    // This would need to call a parent function to add a new factor
    // For now, we'll just log what would happen
    console.log(`Would create new factor: ${factorDetails}`);
  };
  
  // Prepare data for the AI Guide Modal
  const surveyDataForAI = {
    title: title || "",
    description: description || "",
    items: Array.isArray(droppedItems) ? droppedItems : [],
    factors: Array.isArray(factors) ? factors.map(factor => ({
      ...factor,
      questionIds: Array.isArray(factor.questionIds) ? factor.questionIds : []
    })) : [],
    // Functions for updating the survey
    updateQuestion: handleUpdateQuestion,
    addQuestion: handleAddAISuggestedQuestion,
    moveQuestionToFactor: handleMoveQuestionToFactor,
    addFactor: handleAddFactor
  };

  console.log("droppedItems", droppedItems)
  console.log("selectedItem", selectedItem)

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Content>
        {/* The draggable library menu (left) */}
        <Droppable droppableId="toolMenu" isDropDisabled={true}>
          {(provided) => (
            <ToolMenu ref={provided.innerRef} {...provided.droppableProps}>
              <MenuTitle>Survey Components</MenuTitle>
              
              <ButtonsContainer>
                <LibraryButton onClick={() => setIsLibraryModalOpen(true)}>
                  Question Library
                </LibraryButton>
                
                <AIGuideButton onClick={() => setIsAIGuideModalOpen(true)}>
                  AI Survey Guide
                </AIGuideButton>
              </ButtonsContainer>

              {Object.entries(groupedTypes).map(([category, typeKeys]) => (
                <React.Fragment key={category}>
                  <CategoryHeading>
                    {category === "core" ? "Core Components" : "Additional Components"}
                  </CategoryHeading>
                  {typeKeys.map((typeKey) => {
                    const overallIndex = allToolKeys.indexOf(typeKey);
                    const typeDef = componentTypes[typeKey];
                    return (
                      <Draggable
                        key={`tool-${typeKey}`}
                        draggableId={`tool-${typeKey}`}
                        index={overallIndex}
                      >
                        {(provided, snapshot) => (
                          <LibraryItem
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <Icon>{typeDef.icon()}</Icon>
                            <Text>{typeDef.name}</Text>
                          </LibraryItem>
                        )}
                      </Draggable>
                    );
                  })}
                </React.Fragment>
              ))}

              {provided.placeholder}
            </ToolMenu>
          )}
        </Droppable>

        <SurveyContainer>
          {/* The main area where components get dropped (right side) */}
          <Droppable droppableId="componentArea">
            {(provided, snapshot) => (
              <ComponentArea
                ref={provided.innerRef}
                {...provided.droppableProps}
                isDraggingOver={snapshot.isDraggingOver}
              >
                <TitleInput
                  type="text"
                  placeholder="Enter survey title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                <DescriptionInput
                  placeholder="Enter survey description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                />

                {droppedItems.length === 0 && (
                  <EmptyMsg>Drag and drop components here</EmptyMsg>
                )}

                {/* Map through dropped items, ensuring each has a unique key and draggableId */}
                {droppedItems.map((item, index) => (
                  <Draggable 
                    key={item.uniqueId} 
                    draggableId={item.uniqueId}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <AnimatedWrapper>
                        <ComponentElement
                          component={item}
                          provided={provided}
                          snapshot={snapshot}
                          factor={findFactor(item.uniqueId, factors)}
                          onClick={() => handleSelectItem(item)}
                          isSelected={selectedItem?.uniqueId === item.uniqueId}
                          onDuplicate={() => handleDuplicateItem(item)}
                          index={index}
                        />
                      </AnimatedWrapper>
                    )}
                  </Draggable>
                ))}

                {provided.placeholder}
              </ComponentArea>
            )}
          </Droppable>
          
          {/* Post Survey Completion Section */}
          <PostSurveySection>
            <SectionTitle>Post Survey Completion</SectionTitle>
            <SectionDescription>
              Drag and drop end screen components here. These screens will be shown after the survey is completed.
            </SectionDescription>
            
            <Droppable droppableId="endScreenArea">
              {(provided, snapshot) => (
                <EndScreenArea
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  isDraggingOver={snapshot.isDraggingOver}
                >
                  {endScreenItems.length === 0 && (
                    <EmptyMsg>Drag and drop end screen components here</EmptyMsg>
                  )}

                  {/* Map through end screen items, ensuring each has a unique key and draggableId */}
                  {endScreenItems.map((item, index) => (
                    <Draggable 
                      key={item.uniqueId} 
                      draggableId={item.uniqueId}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <AnimatedWrapper>
                          <ComponentElement
                            component={item}
                            provided={provided}
                            snapshot={snapshot}
                            factor={findFactor(item.uniqueId, factors)}
                            onClick={() => handleSelectItem(item)}
                            isSelected={selectedItem?.uniqueId === item.uniqueId}
                            onDuplicate={() => handleDuplicateItem(item, true)}
                            index={index}
                          />
                        </AnimatedWrapper>
                      )}
                    </Draggable>
                  ))}

                  {provided.placeholder}
                </EndScreenArea>
              )}
            </Droppable>
          </PostSurveySection>
        </SurveyContainer>
      </Content>

      {/* Question Library Modal */}
      <QuestionLibraryModal
        isOpen={isLibraryModalOpen}
        onClose={() => setIsLibraryModalOpen(false)}
        componentTypes={componentTypes}
        onAddQuestion={handleAddQuestionFromLibrary}
        categories={categories}
      />
      
      {/* AI Survey Guide Modal */}
      <AISurveyGuideModal
        isOpen={isAIGuideModalOpen}
        onClose={() => setIsAIGuideModalOpen(false)}
        surveyData={surveyDataForAI}
      />
    </DragDropContext>
  );
});

export default SurveyQuestionsTab;


/* --- STYLES --- */

// Example additional styling
const MenuSubtitle = styled.h4`
  margin-top: 20px;
  margin-bottom: 10px;
  font-size: 0.9rem;
  color: #555;
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  font-family: "Raleway", sans-serif;
  position: relative;
`;

const Content = styled.div`
  display: flex;
  width: 100%;
  margin-top: 20px;
`;

const ToolMenu = styled.div`
  width: 250px;
  padding: 10px;
`;

const MenuTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 20px;
`;

const LibraryItem = styled.div`
  font-family: "Raleway", sans-serif;
  padding: 10px;
  display: flex;
  align-items: center;
  margin-bottom: 5px;
  cursor: pointer;
  position: relative;
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 5px;

  &:hover {
    background-color: #f9f9f9;
    .drag-handle {
      opacity: 1;
    }
  }
`;

const Icon = styled.div`
  height: 18px;
  margin-right: 10px;
`;

const Text = styled.div`
  flex: 1;
`;

const SurveyContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 20px;
  padding-bottom: 60px; /* Added spacing at the bottom */
`;

const PostSurveySection = styled.div`
  margin-top: 30px;
  padding: 0;
  border-top: 2px dashed #d0d0d0;
  width: 100%;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 20px 0 10px;
  color: #4a90e2;
  padding: 0 10px;
`;

const SectionDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 15px;
  padding: 0 10px;
`;

const EndScreenArea = styled.div`
  padding: 10px;
  border-radius: 10px;
  min-height: 120px;
  background-color: ${({ isDraggingOver }) => (isDraggingOver ? "#f0f7ff" : "#f5f9ff")};
  position: relative;
  border: 1px dashed #a0c0e7;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 620px; /* Match width of survey components */
  margin: 0 auto; /* Center the container */
`;

const ComponentArea = styled.div`
  flex: 1;
  padding: 10px;
  border-radius: 10px;
  min-height: 400px;
  background-color: ${({ isDraggingOver }) => (isDraggingOver ? "#f5f5f5" : "#fff")};
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/* Simple fade-in for newly dropped items */
const fadeIn = keyframes`
  from {
    opacity: 0; 
    transform: translateY(5px);
  }
  to {
    opacity: 1; 
    transform: translateY(0);
  }
`;

const AnimatedWrapper = styled.div`
  animation: ${fadeIn} 0.3s ease forwards;
`;

// Add these new styled components at the top of your file (or in a separate styles file)
const TitleInput = styled.input`
font-family: "Raleway", sans-serif;
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 8px;
  border: none;
  outline: none;
  background: transparent;
  width: 600px
`;

const DescriptionInput = styled.textarea`
font-family: "Raleway", sans-serif;
  font-size: 14px;
  border: none;
  outline: none;
  background: transparent;
  width: 600px;
  resize: none;
`;


const CategoryHeading = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: #555;
    margin-top: 20px;
`

// Add the Library Button style
const LibraryButton = styled.button`
  width: 100%;
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 10px;
  transition: background-color 0.2s;
  font-family: "Raleway", sans-serif;
  
  &:hover {
    background-color: #3a7bc8;
  }
`;

const AIGuideButton = styled(LibraryButton)`
  background-color: #8e44ad;
  
  &:hover {
    background-color: #7d3c98;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const EmptyMsg = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #888;
  text-align: center;
  margin-top: 40px;
`;